import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getDuplicateTotal, nextUnitPriceLabel, chooseSubtitle } from "../../../utils/modifierPricing";
import { C } from "../../../utils/designTokens";
import { GroupHeader, OptionList, OptionRow, StepperRow, addPriceLabel, domId } from "./OptionParts";

/**
 * Renders the groups revealed by a selected add-on, to any depth.
 *
 * Two things feed it, and they render identically:
 *   - an item-as-modifier brings the add-on item's own modifier groups
 *     (picking "Fruit Tea" reveals its Sweetness / Ice groups)
 *   - a plain modifier can carry follow-up groups attached to its placement
 *     (picking "Pearls" reveals a Sweetness group)
 *
 * A nested group is the SAME group as at the top of the item, so it keeps all
 * of its own rules: duplicate quantities with +/- steppers, tiered "1st FREE"
 * pricing, min/max choices, and 86 state. The maths comes from
 * utils/modifierPricing, shared with the top-level list so the two cannot drift.
 *
 * Nesting is unlimited: any modifier inside a nested group can itself carry
 * further groups, and they open as the customer picks their way down.
 *
 * Self-contained — manages its own selection state and reports
 * { variant, sels, subtotal, childSelections } up so the parent can price the
 * line and ship the order. Selections are keyed by PATH ("groupId|modifierId|
 * groupId|...") rather than by group id, so the same group appearing under two
 * different picks keeps two independent answers.
 */
function childOptPrice(gm, variantTitle) {
  if (gm.priceOverrideMode === "FREE") return 0;
  if (gm.priceOverrideMode === "VARIANT_BASED" && gm.variantPrices) {
    const vp = typeof gm.variantPrices === "string" ? JSON.parse(gm.variantPrices) : gm.variantPrices;
    if (variantTitle && vp[variantTitle] !== undefined) return parseFloat(vp[variantTitle]) || 0;
    const k = variantTitle && Object.keys(vp).find((x) => x.toLowerCase() === variantTitle.toLowerCase());
    if (k) return parseFloat(vp[k]) || 0;
    const vals = Object.values(vp).map((v) => parseFloat(v));
    return vals.length ? Math.min(...vals) : 0;
  }
  return parseFloat(gm.fixedPrice || 0) || 0;
}

// "Choose 1", "Choose up to 2" — same wording as the top-level list.
function nestedConstraintText(cg) {
  const min = cg.minSelections || 0;
  const max = cg.selectionType === "SINGLE" ? 1 : cg.maxSelections;
  if (cg.selectionType === "EXACTLY" && cg.exactSelections) {
    return `Choose exactly ${cg.exactSelections}`;
  }
  return chooseSubtitle(min, max);
}

const sortByOrder = (groups) =>
  [...(groups || [])].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

// The groups a picked member reveals: an item member's own modifier groups, or
// the follow-up groups attached to a plain modifier's placement.
const memberNestedGroups = (gm) =>
  (gm?.nestedGroups && gm.nestedGroups.length > 0)
    ? gm.nestedGroups
    : (gm?.item?.modifierGroups || []);

const groupPath = (prefix, groupId) => (prefix ? `${prefix}|${groupId}` : String(groupId));

/**
 * Walk the selected tree: total it up and turn it into the order-ready
 * childSelections shape, which nests exactly as deep as the customer went.
 * Quantities are priced through the group's duplicate rule, matching what the
 * server charges.
 */
export function buildNestedSelection(groups, sels, prefix, variant) {
  let subtotal = 0;
  const childSelections = [];

  sortByOrder(groups).forEach((cg) => {
    const path = groupPath(prefix, cg._id);
    const picks = sels[path] || [];
    if (picks.length === 0) return;

    // Flat group fee, charged once when anything in the group is picked.
    subtotal += parseFloat(cg.additionalCost || 0) || 0;

    const selectedModifiers = picks.map((p) => {
      const qty = p.quantity || 1;
      // What this pick actually costs under the group's rules — a "1st FREE"
      // tier means the base price is NOT what gets charged. Carried so the cart
      // can print the same number that went into the total; `price` stays the
      // base unit price because that is what the order ships and the server
      // re-prices from.
      const lineTotal = getDuplicateTotal(parseFloat(p.price) || 0, qty, cg);
      subtotal += lineTotal;

      const member = (cg.modifiers || []).find(
        (m) => String(m.modifier?._id ?? m.item?._id) === String(p.modifierId)
      );
      const deeper = memberNestedGroups(member);
      let nested = null;
      if (deeper.length > 0) {
        nested = buildNestedSelection(deeper, sels, `${path}|${p.modifierId}`, variant);
        // Per unit of this pick, like the server (priceChildModifierGroups).
        subtotal += nested.subtotal * qty;
      }

      return {
        modifierId: p.modifierId,
        modifierName: p.modifierName,
        price: p.price,
        quantity: qty,
        lineTotal,
        ...(nested && nested.childSelections.length > 0
          ? { childSelections: nested.childSelections }
          : {}),
      };
    });

    childSelections.push({
      modifierGroupId: cg._id,
      modifierGroupName: cg.name,
      // Display only — Checkout maps the order payload field by field, so this
      // and lineTotal never reach the server.
      groupFee: parseFloat(cg.additionalCost || 0) || 0,
      selectedModifiers,
    });
  });

  return { subtotal, childSelections };
}

const nestIndentSx = { ml: "11px", pl: "14px", borderLeft: `2px solid ${C.border}` };

/**
 * One level of groups. Renders its own picks, then recurses under each picked
 * member that reveals more. Indentation makes the depth legible.
 */
const NestedGroupList = ({ groups, prefix, sels, variant, currencySymbol, onToggle, onChangeQty }) => {
  const { t } = useTranslation();
  return (
  <>
    {sortByOrder(groups).map((cg) => {
      const cgMods = (cg.modifiers || []).filter((m) => !m.isHidden && (m.modifier || m.item));
      if (cgMods.length === 0) return null;
      const path = groupPath(prefix, cg._id);
      const picks = sels[path] || [];
      const isSingle = cg.selectionType === "SINGLE" || cg.maxSelections === 1;
      const groupFee = parseFloat(cg.additionalCost || 0) || 0;
      // Duplicate mode: quantity steppers instead of toggles, exactly as the
      // same group behaves at the top of the item. "Choose up to 2" caps TOTAL
      // UNITS there — each repeat counts — not the number of different options,
      // so two of one topping already fills a group of 2.
      const perItemMax = cg.maxDuplicates || 99;
      const groupMax = cg.maxSelections || 99;
      // A full "Choose up to N" dims the unpicked rows; toggle() still says why.
      const pickMax = cg.selectionType === "EXACTLY" ? cg.exactSelections : cg.maxSelections;
      const atMax = !isSingle && Boolean(pickMax) && picks.length >= pickMax;
      const titleId = domId("nested", path, "title");

      return (
        <Box key={path} sx={{ mt: 1.5 }}>
          <GroupHeader
            compact
            titleId={titleId}
            title={cg.name}
            subTitle={nestedConstraintText(cg)}
            status={(cg.minSelections || 0) >= 1 ? t("required") : t("optional")}
            priceLabel={groupFee > 0 ? `+${currencySymbol}${groupFee.toFixed(2)}` : null}
          />

          <OptionList compact role={!cg.allowDuplicates && isSingle ? "radiogroup" : "group"} labelledBy={titleId}>
          {cgMods.map((gm) => {
            const entity = gm.modifier || gm.item;
            const basePrice = childOptPrice(gm, variant);
            const pick = picks.find((s) => String(s.modifierId) === String(entity._id));
            const checked = Boolean(pick);
            const qty = pick?.quantity || 0;
            const deeper = memberNestedGroups(gm);
            const is86ed = gm.modifier?.libraryStatus?.is86ed || gm.item?.is86ed;

            if (cg.allowDuplicates) {
              // Mirrors the top-level stepper: + stays tappable when the GROUP
              // cap is what blocks, so the customer gets told why instead of
              // meeting a dead button. Only the per-option cap disables it.
              const canIncrease = qty < perItemMax && (qty > 0 || picks.length < groupMax);
              return (
                <React.Fragment key={`NESTED_${path}_${gm._id}`}>
                  <StepperRow
                    compact
                    name={entity.name}
                    price={nextUnitPriceLabel(basePrice, qty, cg, currencySymbol)}
                    qty={qty}
                    onAdd={() => onChangeQty(path, cg, gm, 1)}
                    onRemove={() => onChangeQty(path, cg, gm, -1)}
                    canAdd={canIncrease && !is86ed}
                    canRemove={qty > 0 && !is86ed}
                    disabled={is86ed}
                  />
                  {qty > 0 && deeper.length > 0 && (
                    <Box sx={nestIndentSx}>
                      <NestedGroupList
                        groups={deeper}
                        prefix={`${path}|${entity._id}`}
                        sels={sels}
                        variant={variant}
                        currencySymbol={currencySymbol}
                        onToggle={onToggle}
                        onChangeQty={onChangeQty}
                      />
                    </Box>
                  )}
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={`NESTED_${path}_${gm._id}`}>
                <OptionRow
                  compact
                  kind={isSingle ? "radio" : "checkbox"}
                  checked={checked}
                  disabled={is86ed}
                  faded={atMax && !checked}
                  onClick={() => !is86ed && onToggle(path, cg, gm)}
                  name={entity.name}
                  price={addPriceLabel(basePrice, currencySymbol)}
                />
                {checked && deeper.length > 0 && (
                  <Box sx={nestIndentSx}>
                    <NestedGroupList
                      groups={deeper}
                      prefix={`${path}|${entity._id}`}
                      sels={sels}
                      variant={variant}
                      currencySymbol={currencySymbol}
                      onToggle={onToggle}
                      onChangeQty={onChangeQty}
                    />
                  </Box>
                )}
              </React.Fragment>
            );
          })}
          </OptionList>
        </Box>
      );
    })}
  </>
  );
};

// onMaxMessage surfaces cap warnings through the modal's snackbar, so a
// follow-up group tells the customer why it stopped exactly as the top-level
// groups do.
const NestedItemGroups = ({ option, currencySymbol, value, onChange, onMaxMessage }) => {
  const nestedGroups = option.nestedGroups || [];

  const subVariantKeys =
    option.itemPricingMode === "VARIANTS" && option.itemVariantPrices
      ? Object.keys(
          typeof option.itemVariantPrices === "string"
            ? JSON.parse(option.itemVariantPrices)
            : option.itemVariantPrices
        )
      : [];

  const [variant, setVariant] = useState(
    value?.variant || (subVariantKeys.length > 0 ? subVariantKeys[0] : null)
  );
  const [sels, setSels] = useState(value?.sels || {});

  // Build the payload (subtotal + order-ready childSelections) and report up.
  const report = (nextSels, nextVariant) => {
    const { subtotal, childSelections } = buildNestedSelection(nestedGroups, nextSels, "", nextVariant);
    onChange({ variant: nextVariant, sels: nextSels, subtotal, childSelections });
  };

  // Report hydrated state once on mount so the parent's price reflects a restored
  // (edit) selection without waiting for user interaction.
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      report(sels, variant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickVariant = (vk) => {
    setVariant(vk);
    report(sels, vk);
  };

  const entryFor = (gm) => {
    const entity = gm.modifier || gm.item;
    return {
      modifierId: entity?._id,
      modifierName: entity?.name,
      price: childOptPrice(gm, variant),
      quantity: 1,
    };
  };

  const toggle = (path, cg, gm) => {
    const entity = gm.modifier || gm.item;
    const modId = entity?._id;
    const current = sels[path] || [];
    const exists = current.findIndex((s) => String(s.modifierId) === String(modId));

    let next;
    if (cg.selectionType === "SINGLE" || cg.maxSelections === 1) {
      next = { ...sels, [path]: [entryFor(gm)] };
    } else if (exists >= 0) {
      const updated = [...current];
      updated.splice(exists, 1);
      next = { ...sels, [path]: updated };
    } else {
      const max = cg.selectionType === "EXACTLY" ? cg.exactSelections : cg.maxSelections;
      if (max && current.length >= max) {
        onMaxMessage?.(`You can only choose up to ${max} from "${cg.name}".`);
        return;
      }
      next = { ...sels, [path]: [...current, entryFor(gm)] };
    }

    setSels(next);
    report(next, variant);
  };

  // Duplicate-quantity groups: +/- adjusts this option's count, dropping it from
  // the selection when it reaches zero. Both caps are enforced the way the
  // top-level list enforces them — the group cap counts TOTAL UNITS (repeats
  // included), and maxDuplicates caps any single option.
  const changeQty = (path, cg, gm, delta) => {
    const entity = gm.modifier || gm.item;
    const modId = entity?._id;
    const current = sels[path] || [];
    const idx = current.findIndex((s) => String(s.modifierId) === String(modId));
    const qty = idx >= 0 ? current[idx].quantity || 1 : 0;
    const nextQty = qty + delta;

    if (delta > 0) {
      const groupMax = cg.maxSelections || 99;
      const perItemMax = cg.maxDuplicates || 99;
      const totalUnits = current.reduce((sum, s) => sum + (s.quantity || 1), 0);
      // Same wording the top-level list uses, so hitting a cap inside a
      // follow-up group explains itself the same way.
      if (totalUnits + delta > groupMax) {
        onMaxMessage?.(`You can only choose up to ${groupMax} from "${cg.name}".`);
        return;
      }
      if (nextQty > perItemMax) {
        onMaxMessage?.(`You can only add up to ${perItemMax} of this item.`);
        return;
      }
    }

    let updated;
    if (nextQty <= 0) {
      updated = current.filter((s) => String(s.modifierId) !== String(modId));
    } else if (idx >= 0) {
      updated = [...current];
      updated[idx] = { ...updated[idx], quantity: nextQty };
    } else {
      updated = [...current, { ...entryFor(gm), quantity: nextQty }];
    }

    const next = { ...sels, [path]: updated };
    setSels(next);
    report(next, variant);
  };

  const sizeTitleId = domId("nested", option._id, "size");
  return (
    <Box sx={{ ...nestIndentSx, pb: 0.5 }}>
      {subVariantKeys.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <GroupHeader compact titleId={sizeTitleId} title="Size" />
          <OptionList compact role="radiogroup" labelledBy={sizeTitleId}>
            {subVariantKeys.map((vk) => (
              <OptionRow
                key={vk}
                compact
                kind="radio"
                checked={variant === vk}
                onClick={() => pickVariant(vk)}
                name={vk}
              />
            ))}
          </OptionList>
        </Box>
      )}
      <NestedGroupList
        groups={nestedGroups}
        prefix=""
        sels={sels}
        variant={variant}
        currencySymbol={currencySymbol}
        onToggle={toggle}
        onChangeQty={changeQty}
      />
    </Box>
  );
};

export default NestedItemGroups;
